import React from "react"

import { CompanyRoleEnumOptions } from "@boundless/bl-api-client/customer/models/CompanyRoleEnumLabels"
import {
  getCustomerCompanyInvitationListQueryKey,
  getCustomerMembershipActivityListQueryKey,
  useCustomerCompanyInvitationCreate,
} from "@boundless/bl-api-client/customer/services"
import { CustomerCompanyInvitationCreateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { SendIcon } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { FormInput } from "@/components/form-elements/FormInput"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

type InviteSchema = z.infer<typeof CustomerCompanyInvitationCreateBody>

interface Props {
  companyUuid: string
  companyName: string
}

/**
 * Invites an email address into one company.
 *
 * `companyName` is spelled out in the copy because a multi-company borrower
 * has no other on-screen confirmation of which company they are inviting into.
 */
export function CompanyInvitationDialog({ companyUuid, companyName }: Props) {
  const [open, setOpen] = React.useState(false)
  const queryClient = useQueryClient()

  const form = useForm<InviteSchema>({
    resolver: zodResolver(CustomerCompanyInvitationCreateBody),
    defaultValues: {
      email: "",
      companyRole: "collaborator",
    },
  })

  const { mutate: invite, isPending } = useCustomerCompanyInvitationCreate({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: getCustomerCompanyInvitationListQueryKey(companyUuid),
        })
        queryClient.invalidateQueries({
          queryKey: getCustomerMembershipActivityListQueryKey(companyUuid),
        })
        toast.success(`Invitation sent to ${variables.data.email}`)
        form.reset()
        setOpen(false)
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Invite Teammate</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a teammate to {companyName}</DialogTitle>
          <DialogDescription>
            They will get an email inviting them to join {companyName}. The
            invitation expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit((data) =>
              invite({ companyUuid, data }),
            )}
          >
            <FormInput
              name="email"
              placeholder="Email"
              type="email"
              helpText="Already has a Boundless account? They'll join with that login."
            />
            <FormSelect<InviteSchema>
              name="companyRole"
              label="Role"
              options={CompanyRoleEnumOptions}
              placeholder="Select a role"
            />
            <p className="text-muted-foreground text-sm">
              Collaborators can view and edit everything. Owners can also manage
              people.
            </p>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              {isPending ? (
                <LoadingButton />
              ) : (
                <Button type="submit">
                  <SendIcon />
                  Send
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
